import React, { useState, useCallback } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import type { Coach, Lesson } from '@court-sync/shared';
import { lessonOverrideService } from '@/services/lessonOverride';
import toast from 'react-hot-toast';

export interface BulkCoachReplaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  academyId: string;
  coaches: Coach[];
  lessons: Lesson[];
  /** Pre-select "coach being covered" when opened from a coach card */
  initialFromCoachId?: string;
  stackLevel?: 'default' | 'elevated' | 'top';
  onSuccess: () => void;
}

export function BulkCoachReplaceModal({
  isOpen,
  onClose,
  academyId,
  coaches,
  lessons,
  initialFromCoachId,
  stackLevel = 'default',
  onSuccess,
}: BulkCoachReplaceModalProps) {
  const [fromCoachId, setFromCoachId] = useState(initialFromCoachId ?? '');
  const [toCoachId, setToCoachId] = useState('');
  const [fromDate, setFromDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [lessonType, setLessonType] = useState<'all' | 'group' | 'private' | 'camp'>('all');
  const [selectedLessonIds, setSelectedLessonIds] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState('');
  const [step, setStep] = useState<'form' | 'preview'>('form');
  const [previewRows, setPreviewRows] = useState<
    Array<{
      sessionId: string;
      lessonId: string;
      lessonName: string;
      sessionDate: string;
      startTime: string;
      endTime: string;
    }>
  >([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = useCallback(() => {
    setStep('form');
    setPreviewRows([]);
    setReason('');
    setToCoachId('');
    setFromCoachId(initialFromCoachId ?? '');
    setSelectedLessonIds(new Set());
    setLessonType('all');
  }, [initialFromCoachId]);

  const handleClose = () => {
    reset();
    onClose();
  };

  React.useEffect(() => {
    if (isOpen) {
      setFromCoachId(initialFromCoachId ?? '');
    }
  }, [isOpen, initialFromCoachId]);

  const runPreview = async () => {
    if (!fromCoachId || !toCoachId || fromCoachId === toCoachId) {
      toast.error('Select two different coaches');
      return;
    }
    setLoadingPreview(true);
    try {
      const lessonIds = selectedLessonIds.size > 0 ? [...selectedLessonIds] : undefined;
      const rows = await lessonOverrideService.previewBulkReplaceCoaches({
        academyId,
        fromDate,
        toDate,
        fromCoachId,
        toCoachId,
        lessonIds,
        ...(lessonType !== 'all' ? { lessonType } : {}),
      });
      setPreviewRows(rows);
      setStep('preview');
      if (rows.length === 0) toast('No matching sessions in this range.');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Preview failed');
    } finally {
      setLoadingPreview(false);
    }
  };

  const confirmBulk = async () => {
    if (!fromCoachId || !toCoachId) return;
    setSubmitting(true);
    try {
      const lessonIds = selectedLessonIds.size > 0 ? [...selectedLessonIds] : undefined;
      const res = await lessonOverrideService.bulkReplaceSessionCoaches({
        academyId,
        fromDate,
        toDate,
        fromCoachId,
        toCoachId,
        lessonIds,
        ...(lessonType !== 'all' ? { lessonType } : {}),
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      });
      toast.success(`Updated ${res.updatedCount} session(s). Coaches notified.`);
      onSuccess();
      handleClose();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Bulk replace failed');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleLesson = (id: string) => {
    setSelectedLessonIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Cover coach over date range" maxWidth="3xl" stackLevel={stackLevel}>
      {step === 'form' ? (
        <div className="space-y-4 text-sm">
          <p className="text-gray-600 dark:text-gray-400">
            Replace the selected coach with a substitute on all matching scheduled sessions in the range (optional lesson
            filter).
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">Coach to cover</label>
              <select
                className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-2"
                value={fromCoachId}
                onChange={(e) => setFromCoachId(e.target.value)}
              >
                <option value="">— Select —</option>
                {coaches.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.firstName} {c.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Substitute coach</label>
              <select
                className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-2"
                value={toCoachId}
                onChange={(e) => setToCoachId(e.target.value)}
              >
                <option value="">— Select —</option>
                {coaches.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.firstName} {c.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">From date</label>
              <input
                type="date"
                className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-2"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">To date</label>
              <input
                type="date"
                className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-2"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium mb-1">Lesson type</label>
              <select
                className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-2"
                value={lessonType}
                onChange={(e) => setLessonType(e.target.value as typeof lessonType)}
              >
                <option value="all">All types</option>
                <option value="group">Group</option>
                <option value="private">Private</option>
                <option value="camp">Camp</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium mb-1">Limit to lessons (optional)</label>
              <div className="max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded p-2 space-y-1">
                {lessons.length === 0 ? (
                  <span className="text-gray-500 text-xs">No lessons loaded.</span>
                ) : (
                  lessons.map((l) => (
                    <label key={l.id} className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={selectedLessonIds.has(l.id)}
                        onChange={() => toggleLesson(l.id)}
                      />
                      <span>{l.name}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium mb-1">Reason (required if any session is in the past)</label>
              <textarea
                className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-2"
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void runPreview()} disabled={loadingPreview}>
              {loadingPreview ? 'Loading…' : 'Preview affected sessions'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4 text-sm">
          <p className="font-medium">{previewRows.length} session(s) will be updated.</p>
          <div className="border border-gray-200 dark:border-gray-600 rounded max-h-48 overflow-y-auto text-xs">
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {previewRows.map((r) => (
                <li key={r.sessionId} className="px-2 py-1.5 flex justify-between gap-2">
                  <span>{r.lessonName}</span>
                  <span className="text-gray-500 shrink-0">
                    {r.sessionDate} {r.startTime}–{r.endTime}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setStep('form')}>
              Back
            </Button>
            <Button type="button" onClick={() => void confirmBulk()} disabled={submitting || previewRows.length === 0}>
              {submitting ? 'Saving…' : 'Confirm & notify coaches'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
